import React from 'react';
import { Box, Typography, Container, Chip, Button, CircularProgress } from '@mui/joy';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useBlogBySlug } from './useBlogs';
import { markdownComponents } from './markdownComponents';

const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};


const BlogPostPage = () => {
  const { slug } = useParams();
  const { post, loading } = useBlogBySlug(slug);

  if (loading) {
    return (
      <Box sx={{ py: 12, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (post === 'not_found' || !post) {
    return (
      <Box sx={{ py: 12, minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography level="h2" sx={{ color: 'text.primary', mb: 3 }}>
              Post not found
            </Typography>
            <Typography level="body1" sx={{ color: 'text.secondary', mb: 4 }}>
              The article you are looking for does not exist or has been moved.
            </Typography>
            <Button
              component={Link}
              to="/blog"
              variant="outlined"
              startDecorator={<ArrowBackIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'text.primary',
                '&:hover': {
                  borderColor: '#2E5BBA',
                  color: '#2E5BBA',
                },
              }}
            >
              Back to Blog
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 12, minHeight: '100vh' }}>
      <Helmet>
        <title>{post.title} | Calvet Technology Solutions</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={`${post.title} | Calvet Technology Solutions`} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
      </Helmet>

      <Container maxWidth="md">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            component={Link}
            to="/blog"
            variant="plain"
            startDecorator={<ArrowBackIcon />}
            sx={{
              mb: 4,
              color: 'text.secondary',
              '&:hover': {
                color: '#2E5BBA',
                background: 'transparent',
              },
            }}
          >
            Back to Blog
          </Button>
        </motion.div>

        {/* Article Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ mb: 6 }}>
            <Typography
              level="h2"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.3,
              }}
            >
              {post.title}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                mb: 3,
              }}
            >
              <Typography
                level="body2"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                {formatDate(post.date)}
              </Typography>
              {post.author && (
                <>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }}
                  />
                  <Typography
                    level="body2"
                    sx={{ color: 'text.secondary', fontWeight: 500 }}
                  >
                    {post.author}
                  </Typography>
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {post.tags && post.tags.map((tag) => (
                <Chip
                  key={tag}
                  size="sm"
                  variant="soft"
                  sx={{
                    background: 'rgba(46, 91, 186, 0.1)',
                    color: '#2E5BBA',
                    border: '1px solid rgba(46, 91, 186, 0.2)',
                  }}
                >
                  {tag}
                </Chip>
              ))}
            </Box>
          </Box>
        </motion.div>

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Box
            sx={{
              '& > *:first-of-type': {
                mt: 0,
              },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {post.content}
            </ReactMarkdown>
          </Box>
        </motion.div>

        {/* Bottom Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box
            sx={{
              mt: 8,
              pt: 4,
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center',
            }}
          >
            <Button
              component={Link}
              to="/blog"
              variant="outlined"
              startDecorator={<ArrowBackIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.2)',
                color: 'text.primary',
                '&:hover': {
                  borderColor: '#2E5BBA',
                  color: '#2E5BBA',
                },
              }}
            >
              All Articles
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default BlogPostPage;
